<template>
  <header ref="headerElement" class="fixed z-40 w-full shadow-md print:hidden">
    <div class="relative z-[2] flex h-[2.125rem] items-center border-b bg-[--header-top-bg-color] px-5 py-1 text-xs">
      <ShipToSelector />
    </div>

    <div class="relative z-[1]">
      <!-- region Default slot -->
      <transition :name="isAnimated ? 'slide-fade-top' : ''" mode="out-in">
        <div v-if="customSlots.default" class="relative z-20 bg-[--header-bottom-bg-color]">
          <component :is="customSlots.default" />
        </div>

        <div
          v-else
          class="relative z-20 flex h-14 w-full items-center justify-between gap-x-2 border-b bg-[--header-bottom-bg-color] sm:gap-x-6"
        >
          <!-- region Left slot -->
          <component :is="customSlots.left" v-if="customSlots.left" />

          <div v-else class="flex h-full items-center">
            <button
              :aria-label="$t('common.labels.main_menu')"
              type="button"
              class="h-full pe-3 ps-5 sm:pe-5"
              @click="mobileMenuVisible = true"
            >
              <VcIcon class="text-primary" name="menu" :size="24" />
            </button>

            <router-link :to="$context.settings.default_return_url ?? '/'">
              <VcImage :src="logoUrl" :alt="$context.storeName" class="h-8" lazy />
            </router-link>
          </div>
          <!-- endregion Left slot -->

          <!-- region Right slot -->
          <component :is="customSlots.right" v-if="customSlots.right" />

          <div v-else class="flex h-full flex-row items-center pr-4">
            <a
              v-if="support_phone_number"
              :aria-label="$t('common.labels.support_phone_number')"
              class="px-1 py-2 xs:px-2"
              :href="`tel:${support_phone_number}`"
            >
              <VcIcon class="text-primary" name="phone" :size="24" />
            </a>

            <button
              :aria-label="$t('common.labels.toggle_search_bar')"
              type="button"
              class="px-1 py-2 xs:px-2"
              @click="toggleSearchBar"
            >
              <VcIcon class="text-primary" name="search" :size="24" />
            </button>

            <ExtensionPointList category="mobileHeader" class="px-1 py-2 xs:px-2" />

            <router-link
              :to="{ name: ROUTES.CART.NAME }"
              :aria-label="$t('common.links.cart')"
              class="px-1 py-2 xs:px-2"
            >
              <span class="relative block">
                <VcIcon class="text-primary" name="cart" :size="24" />

                <transition
                  mode="out-in"
                  enter-from-class="scale-0"
                  leave-to-class="scale-0"
                  enter-active-class="will-change-transform"
                  leave-active-class="will-change-transform"
                >
                  <VcBadge
                    v-if="cart?.itemsQuantity"
                    variant="outline"
                    size="sm"
                    class="absolute -right-2 -top-2 transition-transform"
                    rounded
                    nowrap
                    max-width="none"
                  >
                    {{ $n(cart.itemsQuantity, { style: "decimal", notation: "compact" }) }}
                  </VcBadge>
                </transition>
              </span>
            </router-link>
          </div>
          <!-- endregion Right slot -->
        </div>
      </transition>
      <!-- endregion Default slot -->

      <!-- region Mobile Search Bar -->
      <MobileSearchBar :visible="searchBarVisible" />
      <!-- endregion Mobile Search Bar -->
    </div>
  </header>

  <!-- Height placeholder for mobile header due to fixed position -->
  <div :style="placeholderStyle" class="h-14 print:hidden"></div>

  <!-- Mobile menu -->
  <transition
    enter-from-class="-translate-x-full"
    leave-to-class="-translate-x-full"
    enter-active-class="will-change-transform"
    leave-active-class="will-change-transform"
  >
    <MobileMenu
      v-if="mobileMenuVisible"
      class="transition-transform print:hidden"
      :class="{ 'is-visible': mobileMenuVisible }"
      @close="mobileMenuVisible = false"
    />
  </transition>
</template>

<script setup lang="ts">
import { syncRefs, useCssVar, useElementSize, useScrollLock } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useWhiteLabeling } from "@/core/composables";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import { MODULE_XAPI_KEYS } from "@/core/constants/modules";
import { ROUTES } from "@/router/routes/constants";
import { useShortCart } from "@/shared/cart";
import { useNestedMobileHeader } from "@/shared/layout";
import { useSearchBar } from "@/shared/layout/composables/useSearchBar";
import { ShipToSelector } from "@/shared/ship-to-location";
import MobileMenu from "./mobile-menu/mobile-menu.vue";
import MobileSearchBar from "./mobile-search-bar.vue";
import type { StyleValue } from "vue";

const mobileMenuVisible = ref(false);
const headerElement = ref(null);

const { getSettingValue } = useModuleSettings(MODULE_XAPI_KEYS.MODULE_ID);
const support_phone_number = getSettingValue(MODULE_XAPI_KEYS.SUPPORT_PHONE_NUMBER);

const { customSlots, isAnimated } = useNestedMobileHeader();
const { searchBarVisible, toggleSearchBar } = useSearchBar();

const { height } = useElementSize(headerElement);
const { cart } = useShortCart();
const { logoUrl } = useWhiteLabeling();

const placeholderStyle = computed<StyleValue | undefined>(() =>
  height.value ? { height: height.value + "px" } : undefined,
);

// Exact app header height, kept live so sticky elements elsewhere (e.g. tables with a sticky
// header row) can sit flush below this fixed mobile header instead of under it. VcHeader owns
// this var on desktop (see vc-header.vue).
const appHeaderHeightVar = useCssVar("--vc-app-header-height");

watch(
  height,
  (value) => {
    appHeaderHeightVar.value = `${value}px`;
  },
  { immediate: true },
);

const isScrollLocked = computed(() => mobileMenuVisible.value || searchBarVisible.value);
const scrollLock = useScrollLock(document.body);

syncRefs(isScrollLocked, scrollLock);
</script>
